import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Cpu, Gauge, Webhook, Bug, GitBranch, Award, ShieldCheck, Rocket, Smile, TrendingDown, Target, TrendingUp, RefreshCw } from 'lucide-react';

const technologies = [
  {
    category: 'Automation',
    tools: ['Selenium', 'Cypress', 'Playwright', 'Appium', 'TestNG', 'JUnit'],
    icon: Cpu
  },
  {
    category: 'Performance',
    tools: ['JMeter', 'LoadRunner', 'Gatling', 'K6'],
    icon: Gauge
  },
  {
    category: 'API Testing',
    tools: ['Postman', 'Rest Assured', 'Swagger', 'SoapUI'],
    icon: Webhook
  },
  {
    category: 'Bug Tracking',
    tools: ['Jira', 'Azure DevOps', 'Bugzilla', 'Linear'],
    icon: Bug
  },
  {
    category: 'CI/CD',
    tools: ['Jenkins', 'GitHub Actions', 'GitLab CI', 'Azure DevOps'],
    icon: GitBranch
  }
];

const benefits = [
  { text: 'Improved Software Quality', icon: Award },
  { text: 'Reduced Production Defects', icon: ShieldCheck },
  { text: 'Faster Time to Market', icon: Rocket },
  { text: 'Better Customer Satisfaction', icon: Smile },
  { text: 'Lower Maintenance Costs', icon: TrendingDown },
  { text: 'Higher Test Coverage', icon: Target },
  { text: 'Scalable QA Processes', icon: TrendingUp },
  { text: 'Continuous Quality Improvement', icon: RefreshCw }
];

export const TechBenefits = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="tech" ref={ref} className="py-32 bg-[#0f0f10] relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwdGVhbXxlbnwwfHx8fDE3ODQ3MzAwNzV8MA&ixlib=rb-4.1.0&q=85"
          alt="Technology"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Technologies Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
          >
            06
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Technologies & <span className="text-[#D4AF37]">Tools</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl">
              Industry-leading tools and frameworks
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technologies.map((tech, index) => {
              const Icon = tech.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ scale: 1.02, y: -4 }}
                  className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-5 h-5 text-[#1a1a1a]" strokeWidth={2} />
                    </div>
                    <h3 className="text-xl font-bold text-[#D4AF37]">
                      {tech.category}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-[#0f0f10] text-gray-300 text-sm rounded-sm border border-[#D4AF37]/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-200"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Benefits Section */}
        <div>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
          >
            07
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              What You <span className="text-[#D4AF37]">Gain</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Measurable business value from our QA services
            </p>
          </motion.div>

          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 md:p-12 rounded-sm border border-[#D4AF37]/20">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.05 }}
                    whileHover={{ y: -4, scale: 1.03 }}
                    className="group flex flex-col items-center text-center gap-3 p-4 rounded-sm bg-[#0f0f10]/40 border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 transition-all duration-300"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 md:w-7 md:h-7 text-[#1a1a1a]" strokeWidth={1.75} />
                    </div>
                    <p className="text-xs md:text-sm font-medium text-gray-300 group-hover:text-white transition-colors duration-300 leading-tight">
                      {benefit.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};